import type { MarkingColor } from '#shared/utils/markings/schema'
import { MARKING_HTML_MAX, MARKING_TEXT_MAX } from '#shared/utils/markings/schema'

// Classes must be full literal strings for Tailwind's JIT scanner.
// markClass pins the literal light-mode text color instead of the themed
// `text-primary`: the pastel highlight backgrounds don't flip in dark reading
// mode, so the text on them must stay dark there too.
// Swatches use 30% fills in light mode, 50% in reading dark mode, and
// 2px borders. Check/X icons follow the border color. Unselected swatches fill solid with the
// stroke color on hover; selected ones keep the tint so the icon stays
// visible.
export interface MarkingColorStyle {
  buttonClass: string
  markClass: string
  borderColorClass: string
  hoverButtonClass: string
  iconColorClass: string
}

// Record keyed by MarkingColor, so adding a colour in #shared fails the build
// here until it has styling, instead of silently rendering the fallback.
export const MARKING_COLOR_STYLES: Record<MarkingColor, MarkingColorStyle> = {
  polar: {
    buttonClass: 'bg-polar-300/30 reading-dark:bg-polar-100/50',
    markClass: 'bg-polar-200 text-blurple-500',
    borderColorClass: 'border-polar-300 reading-dark:border-polar-100',
    hoverButtonClass: 'hover:bg-polar-300 reading-dark:hover:bg-polar-100',
    iconColorClass: 'text-polar-300 reading-dark:text-polar-100',
  },
  blurple: {
    buttonClass: 'bg-blurple-400/30 reading-dark:bg-blurple-200/50',
    markClass: 'bg-blurple-50 text-blurple-500',
    borderColorClass: 'border-blurple-400 reading-dark:border-blurple-100',
    hoverButtonClass: 'hover:bg-blurple-400 reading-dark:hover:bg-blurple-100',
    iconColorClass: 'text-blurple-400 reading-dark:text-blurple-100',
  },
  moss: {
    buttonClass: 'bg-moss-300/30 reading-dark:bg-moss-200/50',
    markClass: 'bg-moss-100 text-blurple-500',
    borderColorClass: 'border-moss-300 reading-dark:border-moss-200',
    hoverButtonClass: 'hover:bg-moss-300 reading-dark:hover:bg-moss-200',
    iconColorClass: 'text-moss-300 reading-dark:text-moss-200',
  },
  alert: {
    buttonClass: 'bg-yellow-500/30 reading-dark:bg-alert-50/50',
    markClass: 'bg-alert-50 text-blurple-500',
    borderColorClass: 'border-yellow-500 reading-dark:border-alert-50',
    hoverButtonClass: 'hover:bg-yellow-500 reading-dark:hover:bg-alert-50',
    iconColorClass: 'text-yellow-500 reading-dark:text-alert-50',
  },
  warning: {
    buttonClass: 'bg-warning-80/30 reading-dark:bg-warning-40/50',
    markClass: 'bg-warning-20 text-blurple-500',
    borderColorClass: 'border-warning-80 reading-dark:border-warning-40',
    hoverButtonClass: 'hover:bg-warning-80 reading-dark:hover:bg-warning-40',
    iconColorClass: 'text-warning-80 reading-dark:text-warning-40',
  },
}

/** Unknown or stale stored colours render as this one. */
export const FALLBACK_MARKING_COLOR: MarkingColor = 'polar'

/** Colour given to a marking created implicitly by adding a note. */
export const DEFAULT_NOTE_MARKING_COLOR: MarkingColor = 'blurple'

// Tooltip placement, relative to the pointer. Both tooltips are position:fixed,
// so these are viewport coords and need no scroll offset.
export const SELECTION_TOOLTIP_OFFSET = { x: -100, y: 20 }
export const NOTE_TOOLTIP_OFFSET = { x: -160, y: 16 }

/** Smallest gap kept between a tooltip and the viewport edge. */
export const TOOLTIP_VIEWPORT_MARGIN = 8

// Note indicator: a button in the margin of the line where a marking starts.
export const NOTE_INDICATOR_BUTTON_SIZE = 28
export const NOTE_INDICATOR_GAP = 4
/** Line height the button is centred against, so it sits on the text baseline. */
export const NOTE_INDICATOR_LINE_HEIGHT = 24

/**
 * Let the browser finish updating the selection before reading it — mouseup
 * fires before the selection settles.
 */
export const SELECTION_SETTLE_DELAY_MS = 50

/** Coalesces bursts of container mutations into one rehydration pass. */
export const REHYDRATION_DEBOUNCE_MS = 50

/**
 * Matches the server limit exactly. A lower capture cap would silently drop
 * rich formatting for markings the server would happily accept — MARKING_TEXT_MAX
 * is the real size rule, this is only the headroom the markup needs.
 */
export const MAX_STORED_MARKING_HTML_LENGTH = MARKING_HTML_MAX

export const MARKING_MESSAGES = {
  selectionAcrossWorks: 'Velg tekst fra samme dokument for å lage én markering.',
  selectionEmpty: 'Velg tekst for å lage en markering.',
  selectionFailed: 'Markeringen kunne ikke lages fra dette utvalget. Prøv å velge teksten på nytt.',
  selectionTooLong: `Markeringen er for lang. Velg høyst ${MARKING_TEXT_MAX.toLocaleString('nb-NO')} tegn.`,
  renderFailed: 'Markeringen kunne ikke vises i teksten. Sjekk markeringene i arbeidsområdet.',
  sessionExpired: 'Innloggingen må oppdateres før du kan lagre markeringer. Last siden på nytt.',
  loadFailed: 'Kunne ikke hente markeringene. Lagringen ble ikke sendt. Prøv igjen.',
  writeConflict:
    'Markeringen er endret i en annen fane. Kontroller den oppdaterte markeringen før du prøver igjen.',
  writeFailed:
    'Kunne ikke bekrefte lagringen. Prøv igjen. Et eventuelt notatutkast beholdes i redigeringen.',
} as const
